import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Button } from 'react-bootstrap';

import s from './NetworkSelectionToolbar.css'; // eslint-disable-line css-modules/no-unused-class
import GeneSelectButton from '../GeneSelectButton';
import SearchableSelect from '../SearchableSelect';
import CheckboxMultiSelect from '../CheckboxMultiSelect';
import Tooltip from '../Tooltip';
import Tutorial from '../Tutorial';

/*
 *  The presentational component of the network selection UI. Dropdown boxes
 *  to filter by integration, query genes, and data types.
 */
class NetworkSelectionToolbar extends Component {
  static propTypes = {
    // Selected genes
    genes: PropTypes.arrayOf(PropTypes.object),

    // Array of integrations to select from
    integrations: PropTypes.arrayOf(PropTypes.object),

    // Array of datatypes to select from
    datatypes: PropTypes.arrayOf(PropTypes.object),

    // Title of the integrations (e.g. tissue, biological process)
    integrationTitle: PropTypes.string,

    // Selected datatypes
    selectedDatatypes: PropTypes.arrayOf(PropTypes.object),

    // Selected integration
    selectedIntegration: PropTypes.object, // eslint-disable-line react/forbid-prop-types

    // Should the components be disabled
    disabled: PropTypes.bool,

    // Mapping of term id to relevancy score
    integrationScores: PropTypes.object, // eslint-disable-line react/forbid-prop-types

    // Show the tooltip associated with the submit button by default
    showSubmitTooltip: PropTypes.bool,

    // Callback functions
    // Callback when change in datatypes selected
    onDatatypeChange: PropTypes.func, // eslint-disable-line react/require-default-props

    // Callback when change in integration selected
    onIntegrationChange: PropTypes.func, // eslint-disable-line react/require-default-props

    // Callback when gene selction has changed
    onGenesChange: PropTypes.func, // eslint-disable-line react/require-default-props

    // Callback when network is removed
    onRemoveNetwork: PropTypes.func, // eslint-disable-line react/require-default-props

    // Callback when network is added
    onSubmitNetwork: PropTypes.func, // eslint-disable-line react/require-default-props
  };

  static defaultProps = {
    genes: [],
    integrations: [],
    datatypes: [],
    integrationTitle: 'Integration',
    selectedDatatypes: [],
    selectedIntegration: {},
    disabled: false,
    integrationScores: {},
    showSubmitTooltip: false,
  };

  // Only update if the props/state change
  shouldComponentUpdate(nextProps) {
    return (
      nextProps.integrations !== this.props.integrations ||
      nextProps.genes !== this.props.genes ||
      nextProps.datatypes !== this.props.datatypes ||
      nextProps.selectedDatatypes !== this.props.selectedDatatypes ||
      nextProps.selectedIntegration !== this.props.selectedIntegration ||
      nextProps.integrationScores !== this.props.integrationScores ||
      nextProps.disabled !== this.props.disabled
    );
  }

  getRelevance(idx) {
    const integ = this.props.integrations[idx];
    if (!integ || !integ.context.term) return 0;
    const id = integ.context.term.identifier;
    let score = 0.0;
    if (this.props.integrationScores[id])
      score = this.props.integrationScores[id];
    const bins = Math.round((score - 0.5) / 0.1);
    return bins < 0 ? 0 : bins;
  }

  customStyles = {
    option: (base, { data }) => {
      const ofFive = this.getRelevance(data.value);
      const theContent = {
        0: '""',
        1: '""',
        2: '""',
        3: `'\\f005\\f005\\f005\\f006\\f006'`,
        4: `'\\f005\\f005\\f005\\f005\\f006'`,
        5: `'\\f005\\f005\\f005\\f005\\f005'`,
      };

      return {
        ...base,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        marginRight: '70px',
        '&::after': {
          fontFamily: 'FontAwesome',
          content: theContent[ofFive],
          color: '#ffd800',
          float: 'right',
        },
      };
    },
  };

  render() {
    const {
      datatypes,
      disabled,
      genes,
      integrations,
      integrationTitle,
      integrationScores,
      onDatatypeChange,
      onGenesChange,
      onIntegrationChange,
      onRemoveNetwork,
      onSubmitNetwork,
      selectedIntegration,
      selectedDatatypes,
      showSubmitTooltip,
    } = this.props;

    // Integrations data
    let integ = (obj, idx) => ({
      value: idx,
      label: obj.context.title,
      disabled,
      selected: selectedIntegration?.url === obj.url,
    });
    integ = integ.bind(this);

    const integData = integrations.map(integ);
    let integGroupData = integData;
    const integSuggested = integData
      .filter(d => this.getRelevance(d.value))
      .sort((a, b) => this.getRelevance(b.value) - this.getRelevance(a.value));
    // Add 'Suggested' group if scores are given and high enough
    if (integrationScores && integSuggested.length)
      integGroupData = [
        {
          label: `Suggested Integrations`,
          options: integSuggested,
        },
        {
          label: 'All Integrations',
          options: integData,
        },
      ];

    // Datatypes data
    let types = (obj, idx) => ({
      value: idx,
      label: obj.title,
      selected: selectedDatatypes.indexOf(obj) > -1,
      disabled,
    });
    types = types.bind(this);

    // Add a remove button if callback is given
    let removeButton = null;
    if (onRemoveNetwork)
      removeButton = (
        <div className="form-group">
          {/* eslint-disable-next-line jsx-ally/label-has-for */}
          <label style={{ height: '1em' }} />
          <br />
          <Tooltip
            placement="right"
            text={`Remove this ${integrationTitle} network`}
          >
            <Button
              bsStyle="danger"
              disabled={disabled}
              onClick={() => {
                onRemoveNetwork();
              }}
            >
              <i className="fa fa-times-circle" />
            </Button>
          </Tooltip>
        </div>
      );

    // Add a submit button if a callback is given
    let submitButton = null;
    if (onSubmitNetwork)
      submitButton = (
        <div className="form-group">
          {/* eslint-disable-next-line jsx-ally/label-has-for */}
          <label style={{ height: '1em' }} />
          <br />
          <Tooltip
            show={showSubmitTooltip}
            placement="right"
            text={`Show the predicted ${integrationTitle}-specific interactions for the selected genes`}
          >
            <Button
              bsStyle="primary"
              disabled={disabled}
              onClick={() => {
                onSubmitNetwork(selectedIntegration, selectedDatatypes, genes);
              }}
            >
              <i className="fa fa-plus-circle" /> Add network
            </Button>
          </Tooltip>
        </div>
      );

    return (
      <div className={s.root}>
        <form className={`${s.inline} form-inline`}>
          <div className="form-group">
            <Tooltip
              text={`Genes to predict interactions for in ${integrationTitle}`}
            >
              {/* eslint-disable-next-line jsx-ally/label-has-for */}
              <label>Genes</label>
              <br />
              <GeneSelectButton
                disabled={disabled}
                genes={genes}
                handleSubmit={geneList => {
                  if (onGenesChange)
                    onGenesChange(
                      geneList,
                      selectedIntegration,
                      selectedDatatypes,
                    );
                }}
              />
            </Tooltip>
          </div>
          {/* eslint-disable-next-line react/no-string-refs */}
          <div ref="integrationSelection" className="form-group">
            <Tooltip text={`${integrationTitle} to be predicted`}>
              {/* eslint-disable-next-line jsx-ally/label-has-for */}
              <label>{integrationTitle}</label>
              <br />
              <SearchableSelect
                isDisabled={disabled}
                label={selectedIntegration?.context?.title || 'Tissue'}
                onChange={option => {
                  if (onIntegrationChange)
                    onIntegrationChange(
                      integrations[option.value],
                      genes,
                      selectedDatatypes,
                    );
                }}
                options={integGroupData}
                styles={this.customStyles}
              />
            </Tooltip>
          </div>
          <div className="form-group">
            <Tooltip
              text={`Data types to apply in predicting ${integrationTitle}-specific interactions`}
            >
              {/* eslint-disable-next-line jsx-ally/label-has-for */}
              <label>Data types</label>
              <br />
              <CheckboxMultiSelect
                value={datatypes.map(types).filter(opt => opt.selected)}
                isDisabled={disabled}
                options={datatypes.map(types)}
                onChange={updatedSelection => {
                  let optIdx = null;
                  if (updatedSelection.length < selectedDatatypes.length) {
                    // Find the option that was deselected
                    const optionTitle = selectedDatatypes
                      .map(d => d.title)
                      .find(
                        title =>
                          !updatedSelection.map(d => d.label).includes(title),
                      );
                    optIdx = datatypes.findIndex(d => optionTitle === d.title);
                  } else {
                    // Find the option that was selected
                    optIdx =
                      updatedSelection[updatedSelection.length - 1].value;
                  }

                  const option = datatypes.map(types)[optIdx];

                  if (onDatatypeChange) {
                    onDatatypeChange(
                      datatypes[option.value],
                      Number(option.value),
                      !option.selected,
                      selectedIntegration,
                      genes,
                      selectedDatatypes,
                    );
                  }
                }}
                placeholder={`${
                  datatypes.map(types).filter(opt => opt.selected).length
                }
                   of ${
                     // eslint-disable-next-line react/prop-types
                     datatypes.map(types).length
                   } selected.`}
              />
            </Tooltip>
          </div>
          {submitButton}
          {removeButton}
        </form>
        <Tutorial
          step={4}
          // eslint-disable-next-line react/no-string-refs
          target={this.refs.integrationSelection}
          container={this}
          placement="top"
          title="Explore different tissues and genes"
          text={
            'Change the query genes, tissue of interest, or the data types used in predicting network ' +
            'interactions using this toolbar. By default a relevant tissue is selected for the query genes,' +
            ' based on predicted tissue expression.'
          }
        />
      </div>
    );
  }
}

export default withStyles(s)(NetworkSelectionToolbar);
